import { Component, inject, OnInit, Injector, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { FormlyFieldConfig } from '@ngx-formly/core';

import {
  InvoiceClient,
  InvoiceTenantSettingsViewModel,

} from '@core/api';

import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { TranslateService } from '@ngx-translate/core';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { ConfirmationCodeComponent } from '../confirmation-code/confirmation-code.component';

interface HttpErrorResponse {
  error: string;
  error_description?: string;
}

@Component({
  standalone: false,
  selector: 'app-invoice-settings',
  templateUrl: './invoice-settings.component.html',
  styleUrl: './invoice-settings.component.scss'
})
export class InvoiceSettingsComponent  implements OnInit
{

  @ViewChild('hintValidationUnknown', { static: true }) hintValidationUnknown!: TemplateRef<any>;
  @ViewChild('hintValidated', { static: true }) hintValidated!: TemplateRef<any>;
  @ViewChild('hintNotValidated', { static: true }) hintNotValidated!: TemplateRef<any>;

  cardInvoiceSettings: CardConfig;
  form = new FormGroup({});
  formFields: FormlyFieldConfig[]; 
  formModel: InvoiceTenantSettingsViewModel;
  isLoading: boolean = false;
  dataNotAvailable: boolean = false;

  constructor(
    private router: Router,
    private readonly invoiceClient: InvoiceClient,
    private readonly translateService: TranslateService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly toastrService: ToastrService,
    private readonly dialog: MatDialog
  )
  {
  }

  ngOnInit(): void {

    let headline = this.translateService.instant(
      'menu.tenant_invoices.component.settings'
    );

    this.cardInvoiceSettings = {
      header: {
        title: {
          label: headline,
        },
      },
      footer: {
        button1: {
          label: this.translateService.instant(
            'invoice.settings.label__submit'
          ),
          onClick: () => this.save(),
          disabled: false,
        },
        button2: {
          label: this.translateService.instant(
            'invoice.settings.label__cancel'
          ),
          onClick: () => this.cancel(),
          disabled: false,
        }
      }
    };


    this.breadcrumbService.setBreadcrumbs([{ label: headline }]);

    this.initiateForm();

    this.load();
  }

  
  translate(key: string): string {
    return this.translateService.instant(key);
  }

  initiateForm(): void {
    
    this.formFields = [
      {
        fieldGroup : [
          {
            key: 'communicationEmail',
            type: 'input',
            props: {
              label: this.translateService.instant(
                'invoice.settings.label__communicationEmail'
              ),
              required: false,
              maxLength: 100,
            },
            expressionProperties: {
              'templateOptions.hintStart': (model, formState, field) => {
                field.props.hintStart = field.formControl.dirty ? this.hintValidationUnknown :  
                  this.getEmailHint(this.formModel?.communicationEmail, this.formModel?.communicationEmailValidated);
                  
                return '';
              }
            }
          },
          {
            key: 'communicationEmailButtons',
            fieldGroupClassName: 'row',
            fieldGroup: [
              {
                type: 'button' ,
                className: 'form-button col-4',
                props: {
                  label: this.translateService.instant(
                    'invoice.settings.label__startValidation'
                  ),
                  onClick: (field) => {
                    this.communicationEmailStartValidation();
                  }
                },
                expressionProperties: {
                  'templateOptions.disabled': (model, formState, field) => {
                    const communicationEmail = this.form.get('communicationEmail'); 
                    return this.formModel?.communicationEmailValidated || communicationEmail?.dirty || communicationEmail?.value === undefined || communicationEmail?.value === null || communicationEmail?.value === '';
                  }
                }                      
              },
              {
                type: 'button' ,
                className: 'form-button col-4',
                props: {
                  label: this.translateService.instant(
                    'invoice.settings.label__completeValidation'
                  ),
                  onClick: (field) => {
                    this.communicationEmailCompleteValidation();
                  }
                },
                expressionProperties: {
                  'templateOptions.disabled': (model, formState, field) => {
                    const communicationEmail = this.form.get('communicationEmail'); 
                    return this.formModel?.communicationEmailValidated || communicationEmail?.dirty || communicationEmail?.value === undefined || communicationEmail?.value === null || communicationEmail?.value === '';
                  }
                }                      
              }
            ]
          }
        ]
      },
      {
        fieldGroup : [
          {
            key: 'notificationEmail',
            type: 'input',
            props: {
              label: this.translateService.instant(
                'invoice.settings.label__notificationEmail'
              ),
              required: false,
              maxLength: 100,
            },
            expressionProperties: {
              'templateOptions.hintStart': (model, formState, field) => {
                field.props.hintStart = field.formControl.dirty ? this.hintValidationUnknown :  
                  this.getEmailHint(this.formModel?.notificationEmail, this.formModel?.notificationEmailValidated);
                  
                return '';
              }
            }
          },
          {
            key: 'notificationEmailButtons',
            fieldGroupClassName: 'row',
            fieldGroup: [
              {
                type: 'button' ,
                className: 'form-button col-4',
                props: {
                  label: this.translateService.instant(
                    'invoice.settings.label__startValidation'
                  ),
                  onClick: (field) => {
                    this.notificationEmailStartValidation();
                  }
                },
                expressionProperties: {
                  'templateOptions.disabled': (model, formState, field) => {
                    const notificationEmail = this.form.get('notificationEmail'); 
                    return this.formModel?.notificationEmailValidated || notificationEmail?.dirty || notificationEmail?.value === undefined || notificationEmail?.value === null || notificationEmail?.value === '';
                  }
                }                      
              },
              {
                type: 'button' ,
                className: 'form-button col-4',
                props: {
                  label: this.translateService.instant(
                    'invoice.settings.label__completeValidation'
                  ),
                  onClick: (field) => {
                    this.notificationEmailCompleteValidation();
                  }
                },
                expressionProperties: {
                  'templateOptions.disabled': (model, formState, field) => {
                    const notificationEmail = this.form.get('notificationEmail'); 
                    return this.formModel?.notificationEmailValidated || notificationEmail?.dirty || notificationEmail?.value === undefined || notificationEmail?.value === null || notificationEmail?.value === '';
                  }
                }                      
              }
            ]
          }
        ]     
      }
      ,
      {
        key: 'sendInvoiceAsAttachment',
        type: 'checkbox',
        templateOptions: {
          label: this.translateService.instant(
            'invoice.settings.label__invoiceAttachment'
          )
        }
      }
    ];
  }

  getEmailHint(email: string, emailValidated: boolean): TemplateRef<any> | null {

    if (email !== undefined && email !== null && email.trim() !== '') {
      return emailValidated ? this.hintValidated : this.hintNotValidated;
    }

    return null;                
  }

  validateNotificationEmail() {
    alert('Validate notification email functionality to be implemented');
  }

  communicationEmailStartValidation() {
    this.invoiceClient.communicationEmailStartValidation().subscribe({
      next: () => {
        this.toastrService.success(
          this.translateService.instant(
            'invoice.settings.validation_email.send_validation_email_message'
          )
        );
      },
      error: () => {
        this.toastrService.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  communicationEmailCompleteValidation() {

    this.dialog.open(ConfirmationCodeComponent, {
      width: '500px',
      height: 'auto',     
      autoFocus: false,
    })
    .afterClosed()
    .subscribe(confirmationCode => {
      if (confirmationCode) {

        this.invoiceClient.communicationEmailCompleteValidation(confirmationCode).subscribe({
          next: () => {
            this.toastrService.success(
              this.translateService.instant(
                'invoice.settings.validation_code.success_message'
              )
            );
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading = false;
            let message : string;
            let errorDescription = err.error_description?.toLowerCase() ?? '';

            if (errorDescription.includes("expired")){
              message = this.translateService.instant('invoice.settings.validation_code.expired_message');
            } else if (errorDescription.includes("incorrect")){
              message = this.translateService.instant('invoice.settings.validation_code.incorrect_message');
            } else if (errorDescription.includes("too many")) {
              message = this.translateService.instant('invoice.settings.validation_code.too_many_attempts_message');
            }
            else {
              message = this.translateService.instant('generic.errorMessage');
            }

            this.toastrService.error(
              message,
              this.translateService.instant('alert.error')
            );
          }
        });

        this.load();
      }
    });
  }

  notificationEmailStartValidation() {
    this.invoiceClient.notificationEmailStartValidation().subscribe({
      next: () => {
        this.toastrService.success(
          this.translateService.instant(
            'invoice.settings.validation_email.send_validation_email_message'
          )
        );
      },
      error: () => {
        this.toastrService.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  notificationEmailCompleteValidation() {

    this.dialog.open(ConfirmationCodeComponent, {
      width: '500px',
      height: 'auto',     
      autoFocus: false,
    })
    .afterClosed()
    .subscribe(confirmationCode => {
      if (confirmationCode) {

        this.invoiceClient.notificationEmailCompleteValidation(confirmationCode).subscribe({
          next: () => {
            this.toastrService.success(
              this.translateService.instant(
                'invoice.settings.validation_code.success_message'
              )
            );
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading = false;
            let message : string;
            let errorDescription = err.error_description?.toLowerCase() ?? '';

            if (errorDescription.includes("expired")){
              message = this.translateService.instant('invoice.settings.validation_code.expired_message');
            } else if (errorDescription.includes("incorrect")){
              message = this.translateService.instant('invoice.settings.validation_code.incorrect_message');
            } else if (errorDescription.includes("too many")) {
              message = this.translateService.instant('invoice.settings.validation_code.too_many_attempts_message');
            }
            else {
              message = this.translateService.instant('generic.errorMessage');
            }

            this.toastrService.error(
              message,
              this.translateService.instant('alert.error')
            );
          }
        });

        this.load();
      }
    });
  }

  cancel() {
    this.router.navigateByUrl('dashboard');
  }

  private load() {

    this.isLoading = true;

    this.invoiceClient.getSettings().subscribe({
      next: (settings) => {

        this.formModel = settings;

        this.form.reset(this.formModel);

        this.isLoading = false;
      },
      error: (err) => {
        this.router.navigate(['/no-access'], { skipLocationChange: true });
      }
    });
  }

  save() {
    if (this.form.valid) {
      this.updateInvoiceSettings();
    } else {
      this.form.markAllAsTouched();
    }
  }

  updateInvoiceSettings() {

    this.isLoading = true;

    const payload = { ...this.formModel };

    delete payload.communicationEmailValidated
    delete payload.notificationEmailValidated;
    delete payload.organisationName;

    this.invoiceClient.updateSettings(payload).subscribe({
      next: (response) => {
        this.toastrService.success(
          this.translateService.instant(
            'invoice.settings.success_message'
          )
        );
        this.isLoading = false;
        
        this.load();
      },
      error: (err) => {
        let message = this.translateService.instant('generic.errorMessage');
        this.isLoading = false;
          this.toastrService.error(
          message,
          this.translateService.instant('alert.error')
        );
      }
    });
  }

}

